import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import SectionWatermark from '@/components/SectionWatermark';
import { Calendar, ArrowLeft } from 'lucide-react';

export default function BlogPostPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPost = async (attempt = 0) => {
      try {
        const data = await fetchCached(`/blog/${id}`, { ttlMs: 60 * 1000 });
        if (cancelled) return;
        setPost(data);
        setNotFound(false);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 404 && attempt < 1) {
          window.setTimeout(() => {
            loadPost(attempt + 1);
          }, 350);
          return;
        }
        setPost(null);
        setNotFound(status === 404);
        setLoading(false);
      }
    };

    setLoading(true);
    setNotFound(false);
    loadPost();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1E4FA1] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <h2 className="text-2xl font-bold text-[#0F172A] mb-4">{notFound ? 'Post Not Found' : 'Unable to Load Post'}</h2>
        <Link to="/blog" className="text-[#1E4FA1] hover:underline">Back to Blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="pt-20">
      <SectionWatermark
        as="article"
        className="py-16 md:py-24 bg-white"
        contentClassName="max-w-3xl mx-auto px-6 md:px-8"
        opacity={0.1}
      >
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-[#1E4FA1] text-sm font-medium hover:underline mb-8"
          >
            <ArrowLeft size={16} /> Back to Blog
          </Link>

          <h1
            data-testid="blog-post-title"
            className="mscm-section-title font-bold text-[#0F172A] lg:text-5xl"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {post.title}
          </h1>

          <div className="flex min-w-0 items-center gap-2 text-[#94A3B8] text-sm mt-4">
            <Calendar size={16} />
            <span className="min-w-0">{formatDate(post.created_at)}</span>
          </div>

          {post.image_path && (
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                data-testid="blog-post-image"
                src={resolveImageUrl(post.image_path)}
                alt={post.title}
                className="w-full h-auto max-h-[500px] object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
          )}

          <div data-testid="blog-post-content" className="mt-10 prose prose-lg max-w-none">
            {post.content.split('\n').map((paragraph, i) => (
              paragraph.trim() ? (
                <p key={i} className="text-[#475569] leading-relaxed mb-4 text-base">
                  {paragraph}
                </p>
              ) : <br key={i} />
            ))}
          </div>
      </SectionWatermark>
    </div>
  );
}
